import { Slider, Stack } from "@mui/material";
import { Switches } from "mui-rff";
import { useCallback, useEffect, useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { createListCollection } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { Dataset, type WmsMapLayer } from "@/lib/api";
import { useDatasetsQuery, useGetCapabilitiesQuery } from "@/features/admin/hooks/queries";
import Select from "@/components/select/select";
import { required } from "@/features/shared/components/mui-rff-fields/validation-helpers";
import { useAutoSelectSingleOption } from "@/features/shared/hooks/use-auto-select-single-option";

type WMSConfigurationProps = { name: string; initialValue: WmsMapLayer };

const WMSConfiguration = ({ name, initialValue }: WMSConfigurationProps) => {
  const { t } = useTranslation();
  const form = useForm();
  const wmsDatasetQuery = useDatasetsQuery("wms");

  const datasetField = useField<string>(`${name}.wms_dataset_id`, {
    validate: required,
    initialValue: initialValue.wms_dataset_id,
  });
  const layerField = useField(`${name}.layer`, {
    validate: required,
    initialValue: initialValue.layer || "",
  });
  const styleField = useField(`${name}.style`, {
    validate: required,
    defaultValue: initialValue.style || "",
  });
  const OPACITY_DEFAULT = 80;
  const opacityValueField = useField<number | null>(`${name}.opacity_override`, {
    initialValue: initialValue.opacity_override,
  });
  const opacityEnabled = useField(`${name}.opacity_enabled`, {
    initialValue: initialValue.opacity_override !== null,
  });
  useField(`${name}.show_legend`, {
    initialValue: initialValue.show_legend ?? true,
  });

  useEffect(() => {
    form.batch(() => {
      if (!opacityEnabled.input.value) {
        form.change(`${name}.opacity_override`, null);
      } else if (!opacityValueField.input.value) {
        form.change(`${name}.opacity_override`, OPACITY_DEFAULT);
      }
    });
  }, [opacityEnabled.input.value, opacityValueField.input.value]);

  const datasetId = useMemo(() => {
    if (!wmsDatasetQuery.data) return "";

    return (
      wmsDatasetQuery.data.find((dataset) => dataset.id === datasetField.input.value)?.id ??
      ""
    );
  }, [datasetField.input.value, wmsDatasetQuery.data]);

  const layerQuery = useGetCapabilitiesQuery(datasetId, {
    enabled: datasetId.length > 0,
    queryKey: ["getCapabilities", datasetId],
  });

  const wmsQueryOptions = useMemo(() => {
    if (!wmsDatasetQuery.data || !Array.isArray(wmsDatasetQuery.data)) return [];

    return wmsDatasetQuery.data.map((dataset: Dataset) => ({
      label: dataset.name,
      value: dataset.id,
    }));
  }, [wmsDatasetQuery.data]);
  const datasetCollection = createListCollection({
    items: wmsQueryOptions,
  });

  const layerQueryOptions = useMemo(() => {
    if (!layerQuery.data?.layers) return [];

    return layerQuery.data.layers.map((params) => ({
      label: params.title,
      value: params.name,
    }));
  }, [datasetField.input.value, layerQuery.data]);
  const layersCollection = createListCollection({
    items: layerQueryOptions,
  });

  const styleOptions = useMemo(() => {
    if (!layerQuery.data?.layers) return [];

    const selectedLayer = layerQuery.data.layers.find(
      (layer) => layer.name === layerField.input.value,
    );
    if (!selectedLayer || !selectedLayer.styles) return [];

    return selectedLayer.styles.map((style) => ({
      label: style.title,
      value: style.name,
    }));
  }, [layerField.input.value, layerQuery.data]);
  const styleCollection = createListCollection({
    items: styleOptions,
  });

  const onDatasetSelect = useCallback(
    (value: string) => form.change(datasetField.input.name, value),
    [form, datasetField.input.name],
  );
  const onLayerSelect = useCallback(
    (value: string) => form.change(layerField.input.name, value),
    [form, layerField.input.name],
  );
  const onStyleSelect = useCallback(
    (value: string) => form.change(styleField.input.name, value),
    [form, styleField.input.name],
  );

  useAutoSelectSingleOption(
    wmsDatasetQuery.isSuccess ? wmsQueryOptions : [],
    datasetField.input.value,
    onDatasetSelect,
  );
  useAutoSelectSingleOption(
    layerQuery.isSuccess && datasetId ? layerQueryOptions : [],
    layerField.input.value,
    onLayerSelect,
  );
  useAutoSelectSingleOption(
    layerField.input.value.length > 0 ? styleOptions : [],
    styleField.input.value,
    onStyleSelect,
  );

  if (!wmsDatasetQuery.isSuccess) return null;

  return (
    <>
      <Select
        name={`${name}.wms_dataset_id`}
        label={t("admin.widgets.fields.map.wms.dataset")}
        placeholder="Select a dataset"
        collection={datasetCollection}
        value={datasetField.input.value}
        onValueChange={(value) => form.change(datasetField.input.name, value)}
        error={datasetField.meta.touched ? datasetField.meta.error : undefined}
      />

      {datasetField.input.value ? (
        <Select
          name={`${name}.layer`}
          label={t("admin.widgets.fields.map.wms.layers")}
          placeholder="Select a layer"
          collection={layersCollection}
          value={layerField.input.value}
          onValueChange={(value) => form.change(layerField.input.name, value)}
          error={layerField.meta.touched ? layerField.meta.error : undefined}
        />
      ) : null}

      {layerField.input.value.length > 0 ? (
        <Select
          name={`${name}.style`}
          label={t("admin.widgets.fields.map.wms.style")}
          placeholder="Select a style"
          collection={styleCollection}
          value={styleField.input.value}
          onValueChange={(value) => form.change(styleField.input.name, value)}
          error={styleField.meta.touched ? styleField.meta.error : undefined}
        />
      ) : null}

      <Switches
        name={`${name}.show_legend`}
        data={{
          label: "Enable legend",
          value: false,
        }}
      />

      <Stack direction="row" alignItems="center">
        <div style={{ minWidth: "40%" }}>
          <Switches
            name={`${name}.opacity_enabled`}
            data={{
              label: "Override opacity",
              value: false,
            }}
          />
        </div>
        {opacityEnabled.input.value ? (
          <Slider
            {...opacityValueField.input}
            value={opacityValueField.input.value ?? 0}
            min={10}
            max={100}
            valueLabelDisplay="auto"
          />
        ) : null}
      </Stack>
    </>
  );
};

export default WMSConfiguration;
